import {
  useGetAllFeatureSwitches,
  useGetDocumentTypes,
  useGetEnums,
  useGetFinancingConfigs,
  useGetIFinanceHolidays,
  useGetProductProviders,
  useGetProvinceConfigurations,
  useGetTaskTypes,
  useGetTerritories,
  useGetValueListRaw,
} from '@src/data/api/config/config-api'
import { useOptionalUser } from '@src/providers/UserProvider'
import { setReportConflict, setReportVersionDeprecated } from '@src/services/api-client'
import { useCallback, useContext, useEffect, useMemo, useState, type FC, type ReactNode } from 'react'
import { AppConfigContext, AppConfigContextValue } from '../contexts/app-config-context'
import { BusinessError } from '../data/types'
import { Conflict } from '../data/types/Conflict'
import { MechanicalWarrantyProvider } from '../data/types/MechanicalWarrantyProvider'

interface AppConfigProviderProps {
  children: ReactNode
}

const DEFAULT_CONFIG: AppConfigContextValue = {
  businessErrors: [],
  globalTechnicalError: null,
  setBusinessErrors: () => {},
  setTechnicalError: () => {},
  resetErrors: () => {},
  conflict: null,
  setConflict: () => {},
  isInitializing: false,
  isDeprecated: false,
  setDeprecated: () => {},
  language: 'fr',
  setLanguage: () => {},
  creditRefusalReasons: null,
  merchantTags: null,
  territories: [],
  financingConfigs: [],
  provinceConfigurations: {},
  productsProviders: [],
  mechanicalWarrantyProvider: {} as MechanicalWarrantyProvider,
  iFinanceHolidays: [],
  jobTypeEnum: {},
  otherIncomeTypesEnum: {},
  applicantTypeEnum: {},
  financingProgramEnum: {},
  autoPlanEnums: {},
  personalLoanPlanEnums: {},
  ifxPlanEnums: {},
  b2cPlanEnums: {},
  creditRslaPlanEnums: {},
  vlvrPlanEnums: {},
  documentTypes: {},
  taskTypes: {},
  featureSwitchInfo: null,
}

const AppConfigLoader: FC<AppConfigProviderProps> = ({ children }) => {
  const currentYear = new Date().getFullYear()

  // Local state for errors, conflict, language
  const [businessErrors, setBusinessErrorsState] = useState<BusinessError[]>([])
  const [globalTechnicalError, setTechnicalErrorState] = useState<string | null>(null)
  const [conflict, setConflictState] = useState<Conflict | null>(null)
  const [language, setLanguageState] = useState<string>('fr')
  const [isDeprecated, setIsDeprecatedState] = useState<boolean>(false)

  // Fetch value lists (raw DTOs)
  const [creditRefusalReasonsDto, isFetchingCreditRefusalReasons] = useGetValueListRaw('CreditRefusalReasons')
  const [merchantTagsDto, isFetchingMerchantTags] = useGetValueListRaw('Tags')

  // Fetch all config data with React-Query
  const [territories, isFetchingTerritories] = useGetTerritories()
  const [productProviders, isFetchingProductProviders] = useGetProductProviders()
  const [enums, isFetchingEnums] = useGetEnums()
  const [documentTypes, isFetchingDocumentTypes] = useGetDocumentTypes()
  const [financingConfigs, isFetchingFinancingConfigs] = useGetFinancingConfigs()
  const [provinceConfigurations, isFetchingProvinceConfigurations] = useGetProvinceConfigurations()
  const [taskTypes, isFetchingTaskTypes] = useGetTaskTypes()
  const [holidays, isFetchingHolidays] = useGetIFinanceHolidays(currentYear)
  const [featureSwitches, isFetchingFeatureSwitches] = useGetAllFeatureSwitches()

  // Calculate overall loading state
  const isInitializing =
    isFetchingCreditRefusalReasons ||
    isFetchingMerchantTags ||
    isFetchingTerritories ||
    isFetchingProductProviders ||
    isFetchingEnums ||
    isFetchingDocumentTypes ||
    isFetchingFinancingConfigs ||
    isFetchingProvinceConfigurations ||
    isFetchingTaskTypes ||
    isFetchingHolidays ||
    isFetchingFeatureSwitches

  // Callbacks for state setters
  const setBusinessErrors = useCallback((errors: BusinessError[]) => {
    setBusinessErrorsState(errors)
  }, [])

  const setTechnicalError = useCallback((error: string | null) => {
    setTechnicalErrorState(error)
  }, [])

  const resetErrors = useCallback(() => {
    setBusinessErrorsState([])
    setTechnicalErrorState(null)
  }, [])

  const setConflict = useCallback((newConflict: Conflict | null) => {
    setConflictState(newConflict)
  }, [])

  const setLanguage = useCallback((lang: string) => {
    setLanguageState(lang)
  }, [])

  const setDeprecated = useCallback((deprecated: boolean) => {
    setIsDeprecatedState(deprecated)
  }, [])

  // Register the version deprecated callback for API responses
  useEffect(() => {
    setReportVersionDeprecated(() => setIsDeprecatedState(true))
  }, [])

  // Register the conflict callback for 409 responses
  useEffect(() => {
    setReportConflict((conflict) => setConflictState(conflict))
  }, [])

  // Build context value
  const contextValue = useMemo<AppConfigContextValue>(
    () => ({
      // Error state
      businessErrors,
      globalTechnicalError,
      setBusinessErrors,
      setTechnicalError,
      resetErrors,

      // Conflict state
      conflict,
      setConflict,

      // Loading state
      isInitializing,
      isDeprecated,
      setDeprecated,

      // Language
      language,
      setLanguage,

      // Value lists
      creditRefusalReasons: creditRefusalReasonsDto ?? null,
      merchantTags: merchantTagsDto ?? null,

      // Config data
      territories,
      financingConfigs,
      provinceConfigurations,
      productsProviders: productProviders,
      mechanicalWarrantyProvider: {} as MechanicalWarrantyProvider, // TODO: Add hook if needed
      iFinanceHolidays: holidays,

      // Enums
      jobTypeEnum: enums.JobTypeEnum ?? {},
      otherIncomeTypesEnum: enums.OtherIncomeTypesEnum ?? {},
      applicantTypeEnum: enums.ApplicantType ?? {},
      financingProgramEnum: enums.FinancingProgramEnum ?? {},
      autoPlanEnums: enums.AutoPlanEnums ?? {},
      personalLoanPlanEnums: enums.PersonalLoanPlanEnums ?? {},
      ifxPlanEnums: enums.IfxPlanEnums ?? {},
      b2cPlanEnums: enums.B2cPlanEnums ?? {},
      creditRslaPlanEnums: enums.CreditRslaPlanEnums ?? {},
      vlvrPlanEnums: enums.VlvrPlanEnums ?? {},

      // Document and Task types
      documentTypes,
      taskTypes,

      // Feature flags
      featureSwitchInfo: featureSwitches ?? null,
    }),
    [
      businessErrors,
      globalTechnicalError,
      setBusinessErrors,
      setTechnicalError,
      resetErrors,
      conflict,
      setConflict,
      isInitializing,
      isDeprecated,
      setDeprecated,
      language,
      setLanguage,
      creditRefusalReasonsDto,
      merchantTagsDto,
      territories,
      financingConfigs,
      provinceConfigurations,
      productProviders,
      holidays,
      enums,
      documentTypes,
      taskTypes,
      featureSwitches,
    ],
  )

  return <AppConfigContext.Provider value={contextValue}>{children}</AppConfigContext.Provider>
}

export const AppConfigProvider: FC<AppConfigProviderProps> = ({ children }) => {
  const user = useOptionalUser()

  if (!user) {
    return <AppConfigContext.Provider value={DEFAULT_CONFIG}>{children}</AppConfigContext.Provider>
  }

  return <AppConfigLoader>{children}</AppConfigLoader>
}

/**
 * Hook to check if app config is still loading
 * Use this in App.tsx to show loading state
 */
export function useIsAppConfigLoading(): boolean {
  const context = useContext(AppConfigContext)
  return context?.isInitializing ?? true
}

// Re-export context hook for convenience
export { useAppConfigContext } from '../contexts/app-config-context'
